import { useState, useEffect } from "react";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, FileText, Search, RefreshCw, Filter, X } from "lucide-react";
import { useLocation } from "wouter";
import { useDateFormat } from "@/contexts/DateFormatContext";
import { useAuth } from "@/_core/hooks/useAuth";

export default function SystemLogs() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { formatDateTime } = useDateFormat();
  const { user, loading } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");

  // Redirect non-admin users to dashboard
  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const { data: logsData, isLoading, refetch } = trpc.auditLogs.list.useQuery({
    page,
    pageSize,
    search: searchQuery || undefined,
    action: actionFilter !== "all" ? actionFilter : undefined,
    level: levelFilter !== "all" ? levelFilter : undefined,
  });

  const logs = logsData?.logs || [];
  const totalPages = logsData?.totalPages || 1;
  const totalCount = logsData?.totalCount || 0;

  const handleClearFilters = () => {
    setSearchQuery("");
    setActionFilter("all");
    setLevelFilter("all");
    setPage(1);
  };

  const getLevelBadge = (level: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      info: "default",
      warning: "secondary",
      error: "destructive",
      success: "outline",
    };

    return (
      <Badge variant={variants[level] || "default"}>
        {level.toUpperCase()}
      </Badge>
    );
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900 p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{t.common.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      <div className="container mx-auto p-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="mb-6 gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.common.back}
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <FileText className="h-9 w-9 text-purple-600" />
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {t.systemLogs.title}
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {t.systemLogs.subtitle}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6 backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5 text-purple-600" />
                {t.systemLogs.filters}
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t.systemLogs.refresh}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t.systemLogs.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Action Filter */}
              <div className="w-full md:w-48">
                <Select value={actionFilter} onValueChange={(value) => { setActionFilter(value); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.systemLogs.allActions} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.systemLogs.allActions}</SelectItem>
                    <SelectItem value="order.create">{t.systemLogs.actions.orderCreate}</SelectItem>
                    <SelectItem value="order.update">{t.systemLogs.actions.orderUpdate}</SelectItem>
                    <SelectItem value="project.create">{t.systemLogs.actions.projectCreate}</SelectItem>
                    <SelectItem value="image.upload">{t.systemLogs.actions.imageUpload}</SelectItem>
                    <SelectItem value="user.login">{t.systemLogs.actions.userLogin}</SelectItem>
                    <SelectItem value="user.logout">{t.systemLogs.actions.userLogout}</SelectItem>
                    <SelectItem value="payment.success">{t.systemLogs.actions.paymentSuccess}</SelectItem>
                    <SelectItem value="payment.failed">{t.systemLogs.actions.paymentFailed}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Level Filter */}
              <div className="w-full md:w-40">
                <Select value={levelFilter} onValueChange={(value) => { setLevelFilter(value); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.systemLogs.allLevels} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.systemLogs.allLevels}</SelectItem>
                    <SelectItem value="info">{t.systemLogs.levels.info}</SelectItem>
                    <SelectItem value="warning">{t.systemLogs.levels.warning}</SelectItem>
                    <SelectItem value="error">{t.systemLogs.levels.error}</SelectItem>
                    <SelectItem value="success">{t.systemLogs.levels.success}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              {(searchQuery || actionFilter !== "all" || levelFilter !== "all") && (
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                >
                  <X className="h-4 w-4" />
                  {t.systemLogs.clear}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
          <CardHeader>
            <CardTitle>{t.systemLogs.title}</CardTitle>
            <CardDescription>
              {t.systemLogs.found.replace("{count}", String(totalCount))}
              {logs.length !== totalCount && ` • ${t.systemLogs.pageOf.replace("{page}", String(page)).replace("{total}", String(totalPages))}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg overflow-hidden border border-purple-100 dark:border-purple-900">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                    <TableHead className="font-semibold">{t.systemLogs.columns.timestamp}</TableHead>
                    <TableHead className="font-semibold">{t.systemLogs.columns.level}</TableHead>
                    <TableHead className="font-semibold">{t.systemLogs.columns.action}</TableHead>
                    <TableHead className="font-semibold">{t.systemLogs.columns.user}</TableHead>
                    <TableHead className="font-semibold">{t.systemLogs.columns.details}</TableHead>
                    <TableHead className="font-semibold">{t.systemLogs.columns.ip}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {t.systemLogs.noneFound}
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log: any) => (
                      <TableRow
                        key={log.id}
                        className="hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors"
                      >
                        <TableCell className="font-mono text-sm">
                          {formatDateTime(log.createdAt)}
                        </TableCell>
                        <TableCell>{getLevelBadge(log.level)}</TableCell>
                        <TableCell className="font-medium">{log.action}</TableCell>
                        <TableCell>
                          {log.userName ? (
                            <span className="text-sm">
                              {log.userName}
                              <span className="text-xs text-muted-foreground ml-1">
                                (ID: {log.userId})
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate text-sm" title={log.details || ""}>
                            {log.details || "-"}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {log.ipAddress || "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-purple-200 dark:border-purple-800">
                <div className="text-sm text-muted-foreground">
                  Zeige {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} von {totalCount}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Zurück
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className={page === pageNum ? "bg-purple-600 hover:bg-purple-700" : ""}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
